import {
  type FetchQueryOptions,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import config from 'config';
import type { Country, Language } from 'services/i18n';
import { rumService } from 'services/rum';
import { createAPIError } from 'utils/exceptions/apiError';

import { experimentVariantManager } from 'utils/experimentation';
import { readChallengeHash } from '../otp';
import type { CreateBaseLeadResponse } from '../signup';
import { resolveClubFlowHeaders, resolveUSCFlowHeaders } from './headers';

export type TcDecision =
  | 'accepted'
  | 'no_decision'
  | 'payment_is_too_low'
  | 'unclear_plans'
  | 'unclear_success_fee'
  | 'tc_disagreement'
  | 'know_more';

export type UpdateTcDecision = Exclude<TcDecision, 'no_decision'>;

type VisitLimitsB2c = {
  s: string | null;
  m: string | null;
  l: string | null;
  xl: string | null;
};

type BaseUpdateOppVariables = {
  oppId: string;
  tcVersion: string;
  uscTcVersion?: string;
  clubTcVersion?: string;
  tcUuid: string;
  additionalReasonInfo?: string;
  /**
   * The email the challenge belongs to. The request reads the hash of its challenge when it is sent.
   */
  businessEmail: string;
  /**
   * When `true`, sends the `Wellhub-USC-Flow` header so the BFF resolves the
   * opportunity through the USC flow before applying the update. Transport
   * concern only — it travels as a header, never in the request body.
   */
  isUscFlow?: boolean;
  /**
   * When `true`, sends the `Wellhub-Club-Flow` header so the BFF resolves the
   * opportunity through the Club flow before applying the update. Transport
   * concern only — it travels as a header, never in the request body.
   */
  isClubFlow?: boolean;
};

type AcceptedUpdateOppVariables = BaseUpdateOppVariables & {
  tcDecision: 'accepted';
  tcAcceptanceDatetime: Date;
  tcAcceptedBy: string;
  tcRejectionDatetime?: never;
  tcRejectedBy?: never;
  experimentVariant?: string;
  products?: Array<{ id: string; vat: string }>;
  availableForB2c?: boolean;
  visitLimitsB2c?: Partial<VisitLimitsB2c>;
  ssuCoupon?: string | null;
  wellhubClubIncluded?: boolean;
};

type RejectedUpdateOppVariables = BaseUpdateOppVariables & {
  tcDecision: Exclude<UpdateTcDecision, 'accepted'>;
  tcRejectionDatetime: Date;
  tcRejectedBy: string;
  tcAcceptanceDatetime?: never;
  tcAcceptedBy?: never;
  experimentVariant?: string;
  products?: Array<{ id: string; vat: string }>;
  ssuCoupon?: string | null;
};

export type CommercialCondition = {
  active: boolean;
  discount: number;
  value: number;
};

export type SuccessFee = {
  discount: string;
  value: string;
};

export type Product = {
  id: string;
  freeVisits?: number;
  lateCancel: CommercialCondition;
  noShow: CommercialCondition;
  maxPerUser: number;
  name: string;
  plan: string;
  pricePerVisit: number;
  successFee?: SuccessFee[];
  vat?: string | null;
  clubPricePerVisit?: number | null;
  clubMaxPerUser?: number | null;
  clubDeltaPercentage?: number | null;
};

export type Opp = {
  id: string;
  city: string;
  country: string;
  countryCode: Country;
  currencyIsoCode: string;
  decision: TcDecision;
  leadId: string;
  legalName: string;
  postalCode: string;
  state: string;
  street: string;
  userEmail: string;
  taxId: string;
  language?: Language;
  tcAcceptedBy?: string | null;
  tcRejectedBy?: string | null;
  tcRejectionDatetime?: Date | null;
  tcVersion: string;
  uscTcVersion?: string | null;
  userName: string;
  stageName: string;
  products: Product[];
  tcUuid: string;
  typeAccess: 'view' | 'edit';
  tcAcceptanceDatetime?: Date | null;
  externalSource?: string | null;
  availableForB2c?: boolean | null;
  isOptInB2c?: boolean | null;
  visitLimitsB2c?: VisitLimitsB2c | null;
  ssuCoupon?: string | null;
  isOptInClub?: boolean | null;
  wellhubClubIncluded?: boolean | null;
  clubTcVersion?: string | null;
};

/**
 * What `GET /v1/opportunity/:oppId` answers with no hash: who must prove ownership. It needs no credential, and it never changes with verification or with a decision.
 */
export type OppSummary = {
  oppExternalSource?: string | null;
  oppEmail: string;
  oppId: string;
  oppLeadId: string;
};

type UnverifiedOppResponse = OppSummary & {
  requireAuthentication: true;
};

/** What the same endpoint answers with a confirmed hash. */
export type VerifiedOppResponse = OppSummary & {
  requireAuthentication: false;
  opp: Opp;
};

export type OppResponse = UnverifiedOppResponse | VerifiedOppResponse;

export type OppSearchSignupResponse = CreateBaseLeadResponse & {
  opportunity: {
    id: string;
    businessEmail: string;
  };
};

export type UpdateOppVariables =
  | AcceptedUpdateOppVariables
  | RejectedUpdateOppVariables;

export type OppSearchResponse = {
  id: string;
  leadId: string;
  email: string;
  success: boolean;
};

export type OppQueryVariables = {
  oppId: string;
};

/**
 * The feature flags the verified Opportunity request sends as headers. Both are required, so a reader that forgets one fails to compile.
 */
export type OppFlowFlags = {
  /**
   * When `true`, the request carries the `Wellhub-USC-Flow` header.
   */
  isUscFlow: boolean;
  /**
   * When `true`, the request carries the `Wellhub-Club-Flow` header, and the BFF resolves the Opportunity through the Club flow.
   */
  isClubFlow: boolean;
};

type OppByIdQueryVariables = OppQueryVariables & OppFlowFlags;

type CustomQueryOptions = {
  options?: {
    validateResult?: (data: OppSearchResponse) => void;
  } & Omit<FetchQueryOptions<OppSearchResponse>, 'queryKey' | 'queryFn'>;
};

type SearchOppQueryVariables =
  | { email: string; leadId?: string }
  | { email?: string; leadId: string };

/**
 * Fetches an opportunity by its ID.
 *
 * @async
 * @function getOppById
 * @param oppId - The unique identifier of the opportunity to retrieve.
 * @param emailValidationId - The confirmed hash. Without it the answer is the summary, with `requireAuthentication: true`.
 * @param isUscFlow - When `true`, sends the `Wellhub-USC-Flow` header.
 * @param isClubFlow - When `true`, sends the `Wellhub-Club-Flow` header so the BFF resolves the Club flow.
 * @returns A promise that resolves to the opportunity response containing opportunity details.
 * @throws {APIError} When the API request fails or returns a non-ok status.
 */
async function getOppById(
  oppId: string,
  emailValidationId?: string,
  isUscFlow?: boolean,
  isClubFlow?: boolean,
): Promise<OppResponse> {
  const url = new URL(
    `${config.PARTNERS_SELF_SIGNUP_BFF_URL}/v1/opportunity/${oppId}`,
  );

  if (emailValidationId) {
    url.searchParams.append('emailValidationId', emailValidationId);
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...resolveUSCFlowHeaders(isUscFlow),
      ...resolveClubFlowHeaders(isClubFlow),
    },
  });

  if (!response.ok) {
    throw await createAPIError(response);
  }

  const result: OppResponse = await response.json();

  return result;
}

/**
 * The prefix of every verified Opportunity key, whatever the flags. Invalidation and writes that do not depend on a flag use it to reach every flag variant.
 */
export function getOppQueryKey({ oppId }: OppQueryVariables) {
  return ['opp', oppId] as const;
}

function getOppSummaryQueryKey({ oppId }: OppQueryVariables) {
  return ['opp-summary', oppId] as const;
}

// Neither Opportunity query refetches on its own: the answer changes only when the Partner decides, and the success pages invalidate it then. An invalidated query is stale whatever its `staleTime`, so `query()` reads it again.
const oppQueryBehavior = {
  staleTime: Infinity,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
} as const;

/**
 * The read with no hash, which needs no credential. It says who must prove ownership of the Opportunity, and it stores nothing else.
 */
export function getOppSummaryQueryOptions({ oppId }: OppQueryVariables) {
  return queryOptions({
    queryKey: getOppSummaryQueryKey({ oppId }),
    queryFn: async (): Promise<OppSummary> => {
      const response = await getOppById(oppId);

      return {
        oppExternalSource: response.oppExternalSource,
        oppEmail: response.oppEmail,
        oppId: response.oppId,
        oppLeadId: response.oppLeadId,
      };
    },
    ...oppQueryBehavior,
  });
}

/**
 * The verified answer, and nothing else. Its key holds `oppId` and the flags the request sends, so each flag value has its own entry. The hash is not in the key: it is a credential, and any confirmed hash for the Opportunity's email gets the same answer. The query function reads the summary for the Opportunity's email, then the hash of that email's challenge, then sends the request. It throws when there is no challenge and when the server does not accept the hash, so an answer read without verification is an error state and is never stored as data.
 */
export function getOppByIdQueryOptions({
  oppId,
  isUscFlow,
  isClubFlow,
}: OppByIdQueryVariables) {
  return queryOptions({
    queryKey: [...getOppQueryKey({ oppId }), { isUscFlow, isClubFlow }],
    queryFn: async ({ client }): Promise<VerifiedOppResponse> => {
      const { oppEmail } = await client.query(
        getOppSummaryQueryOptions({ oppId }),
      );
      const emailValidationId = readChallengeHash({
        queryClient: client,
        email: oppEmail,
      });

      if (!emailValidationId) {
        throw new Error('[opp] No challenge for the Opportunity email');
      }

      const response = await getOppById(
        oppId,
        emailValidationId,
        isUscFlow,
        isClubFlow,
      );

      if (response.requireAuthentication) {
        throw new Error('[opp] The server did not accept the hash');
      }

      return response;
    },
    ...oppQueryBehavior,
  });
}

/**
 * Searches for opportunities based on lead ID and/or email address.
 *
 * @param params - The search parameters
 * @param params.leadId - Optional lead ID to filter opportunities
 * @param params.email - Optional email address to filter opportunities
 *
 * @returns Promise that resolves to the opportunity search response
 *
 * @throws {Error} When the API request fails or returns a non-ok status
 */
export async function searchOpp({ leadId, email }: SearchOppQueryVariables) {
  const url = new URL(
    `${config.PARTNERS_SELF_SIGNUP_BFF_URL}/v1/opportunity/search`,
  );

  if (leadId) {
    url.searchParams.append('filter[leadId]', leadId);
  }

  if (email) {
    url.searchParams.append('filter[email]', email);
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    rumService.addError(
      new Error(
        `[searchOpp] Error searching opp, email: ${email} leadId: ${leadId}`,
      ),
    );

    throw new Error(`${response.status} (${response.statusText})`);
  }

  const result: OppSearchResponse = await response.json();

  return result;
}

/**
 * Updates an existing opp with new data using a PATCH request.
 *
 * @async
 * @function updateOpp
 * @param variables - An object containing the step number, flags controlling the form flow, and the updated lead data.
 * @returns A promise resolving to a structure containing the updated lead information along with its self-sign-up ID.
 * @throws Will throw an error if the response is not OK.
 */
async function updateOpp(
  variables: UpdateOppVariables,
  queryClient: QueryClient,
): Promise<void> {
  // `isUscFlow`/`isClubFlow` drive request headers only — keep them out of the body.
  const { isUscFlow, isClubFlow, ...payload } = variables;
  const emailValidationId = readChallengeHash({
    queryClient,
    email: payload.businessEmail,
  });
  const url = `${config.PARTNERS_SELF_SIGNUP_BFF_URL}/v2/opportunity/${payload.oppId}`;
  const experimentVariant = experimentVariantManager.resolveForPayload(
    payload.experimentVariant,
  );
  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...resolveUSCFlowHeaders(isUscFlow),
      ...resolveClubFlowHeaders(isClubFlow),
    },
    body: JSON.stringify({
      ...payload,
      emailValidationId,
      experimentVariant,
    }),
  });

  if (!response.ok) {
    throw await createAPIError(response);
  }

  const result = await response.json();

  // A hash the BFF refuses gets a `200` with `requireAuthentication: true`, and nothing is saved. That is a failure, not a decision.
  if (result.requireAuthentication) {
    throw new Error('[updateOpp] The server did not accept the hash');
  }

  return result;
}

/**
 * Provides a React Query mutation hook for updating an existing opp.
 *
 * @function useUpdateOpp
 * @returns An object containing mutate and status properties from the React Query mutation API.
 */
export function useUpdateOpp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: UpdateOppVariables) =>
      updateOpp(variables, queryClient),
    onSuccess: (_data, variables) => {
      // Sync the cached opp's ssuCoupon with what the PATCH persisted: the opp
      // query never refetches, so a stale cache would re-seed a cleared coupon
      // on reload. Omitted field = untouched on the BFF (ADR 012), no sync.
      if (variables.ssuCoupon === undefined) return;

      // The coupon does not depend on a flag, so every flag variant gets it.
      queryClient.setQueriesData<VerifiedOppResponse>(
        { queryKey: getOppQueryKey({ oppId: variables.oppId }) },
        previous =>
          previous && {
            ...previous,
            opp: { ...previous.opp, ssuCoupon: variables.ssuCoupon },
          },
      );
    },
  });
}

function getSearchOppQueryKey(variables: SearchOppQueryVariables) {
  return ['searchOpp', variables.leadId, variables.email] as const;
}

/**
 * Generates query options for searching opportunity based on the provided lead id or email.
 *
 * @param params - The parameters for the query options.
 * @param params.leadId - The ID of the lead to search for an opportunity.
 * @param params.email - The email associated with the lead to search for an opportunity.
 * @param params.options - Additional query options to customize the behavior of the query.
 *
 * @returns Query result options for searching opportunity.
 */
export function searchOppQueryOptions({
  leadId,
  email,
  options,
}: SearchOppQueryVariables & CustomQueryOptions) {
  const variables = { leadId, email } as SearchOppQueryVariables;

  const queryFn = async () => {
    const result = await searchOpp(variables);

    if (options?.validateResult) {
      options.validateResult(result);
    }

    return result;
  };

  return queryOptions({
    queryKey: getSearchOppQueryKey(variables),
    queryFn,
    enabled: !!leadId || !!email,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...options,
  });
}
